import type { TenantTheme, TenantThemePresetId } from '../types';
import { DEFAULT_FOCUS_OUTLINE, normalizeFocusOutline } from './focusOutline.js';

export const TENANT_THEME_PRESET_IDS: TenantThemePresetId[] = [
  'default',
  'forest',
  'rose',
  'amber',
  'violet',
  'slate',
  'custom',
];

/** Tenant-wide base type size (% of 1rem) for website + /app vitrine. */
export const TENANT_BASE_FONT_SCALE_MIN = 50;
export const TENANT_BASE_FONT_SCALE_MAX = 150;
export const TENANT_BASE_FONT_SCALE_STEP = 5;
export const DEFAULT_TENANT_BASE_FONT_SCALE = 100;

export const DEFAULT_TENANT_THEME: TenantTheme = {
  preset: 'default',
  accent: '#2563eb',
  focusOutlineWidthPx: DEFAULT_FOCUS_OUTLINE.widthPx,
  focusOutlineOffsetPx: DEFAULT_FOCUS_OUTLINE.offsetPx,
  baseFontScale: DEFAULT_TENANT_BASE_FONT_SCALE,
};

export const TENANT_THEME_PRESET_ACCENTS: Record<Exclude<TenantThemePresetId, 'custom'>, string> = {
  default: '#2563eb',
  forest: '#047857',
  rose: '#e11d48',
  amber: '#b45309',
  violet: '#7c3aed',
  slate: '#475569',
};

export const TENANT_THEME_PRESET_LABEL_KEYS: Record<TenantThemePresetId, string> = {
  default: 'settings.themePresetDefault',
  forest: 'settings.themePresetForest',
  rose: 'settings.themePresetRose',
  amber: 'settings.themePresetAmber',
  violet: 'settings.themePresetViolet',
  slate: 'settings.themePresetSlate',
  custom: 'settings.themePresetCustom',
};

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export function isValidAccentColor(value: string): boolean {
  return HEX_COLOR.test(value.trim());
}

export function normalizeTenantThemePreset(value: unknown): TenantThemePresetId {
  return TENANT_THEME_PRESET_IDS.includes(value as TenantThemePresetId)
    ? (value as TenantThemePresetId)
    : 'default';
}

/** Map legacy Appearance typeScale radios → base %. */
export function baseFontScaleFromLegacyTypeScale(typeScale: unknown): number {
  const raw = String(typeScale || '').trim().toLowerCase();
  if (raw === 'sm') return 92;
  if (raw === 'lg') return 112;
  return DEFAULT_TENANT_BASE_FONT_SCALE;
}

export function normalizeBaseFontScale(raw: unknown, fallback = DEFAULT_TENANT_BASE_FONT_SCALE): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  const stepped = Math.round(n / TENANT_BASE_FONT_SCALE_STEP) * TENANT_BASE_FONT_SCALE_STEP;
  return Math.min(
    TENANT_BASE_FONT_SCALE_MAX,
    Math.max(TENANT_BASE_FONT_SCALE_MIN, stepped),
  );
}

/** CSS length for `--sf-font-size-base`. */
export function tenantBaseFontSizeCss(scale: number): string {
  return `calc(1rem * ${normalizeBaseFontScale(scale) / 100})`;
}

function pickThemeExtras(record: Record<string, unknown>): {
  focusOutlineWidthPx: number;
  focusOutlineOffsetPx: number;
  baseFontScale: number;
} {
  const focusOutline = normalizeFocusOutline(
    record.focusOutline
      ?? record.focus_outline
      ?? {
        widthPx: record.focusOutlineWidthPx ?? record.focus_outline_width_px,
        offsetPx: record.focusOutlineOffsetPx ?? record.focus_outline_offset_px,
      },
  );
  const hasBase =
    record.baseFontScale != null
    || record.base_font_scale != null;
  const baseFontScale = hasBase
    ? normalizeBaseFontScale(
      record.baseFontScale ?? record.base_font_scale,
      DEFAULT_TENANT_BASE_FONT_SCALE,
    )
    : baseFontScaleFromLegacyTypeScale(record.legacyTypeScale ?? record.legacy_type_scale);

  return {
    focusOutlineWidthPx: focusOutline.widthPx,
    focusOutlineOffsetPx: focusOutline.offsetPx,
    baseFontScale,
  };
}

/** Mirror of server normalizeTenantTheme. */
export function normalizeTenantTheme(raw: unknown): TenantTheme {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_TENANT_THEME };
  }

  const record = raw as Record<string, unknown>;
  const preset = normalizeTenantThemePreset(record.preset);
  const accentRaw = String(record.accent || '').trim();
  const extras = pickThemeExtras(record);

  if (preset === 'custom') {
    const accent = isValidAccentColor(accentRaw) ? accentRaw : DEFAULT_TENANT_THEME.accent;
    return {
      preset,
      accent,
      ...extras,
    };
  }

  const presetAccent = TENANT_THEME_PRESET_ACCENTS[preset];
  const accent = isValidAccentColor(accentRaw) ? accentRaw : presetAccent;
  return {
    preset,
    accent,
    ...extras,
  };
}

/**
 * When theme JSON has no baseFontScale yet, seed from Appearance.typeScale (legacy).
 * Call once when loading tenant settings into the Theme form.
 */
export function themeWithLegacyAppearanceTypeScale(
  theme: TenantTheme,
  appearanceTypeScale: unknown,
  themeRawHadBase: boolean,
): TenantTheme {
  if (themeRawHadBase) return theme;
  return {
    ...theme,
    baseFontScale: baseFontScaleFromLegacyTypeScale(appearanceTypeScale),
  };
}

export function resolveTenantAccent(theme: TenantTheme): string {
  if (theme.preset === 'custom') {
    return isValidAccentColor(theme.accent) ? theme.accent : DEFAULT_TENANT_THEME.accent;
  }
  return isValidAccentColor(theme.accent)
    ? theme.accent
    : TENANT_THEME_PRESET_ACCENTS[theme.preset];
}

/** Linearize sRGB channel (0–255) for WCAG relative luminance. */
function srgbChannelToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance for `#RRGGBB` (0 = black, 1 = white). */
export function relativeLuminance(hex: string): number {
  const raw = hex.replace('#', '').trim();
  if (raw.length !== 6) return 0;
  const r = srgbChannelToLinear(Number.parseInt(raw.slice(0, 2), 16));
  const g = srgbChannelToLinear(Number.parseInt(raw.slice(2, 4), 16));
  const b = srgbChannelToLinear(Number.parseInt(raw.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Black or white fg that contrasts better with the given background hex.
 * Used for buttons filled with tenant accent.
 */
export function contrastForeground(backgroundHex: string): '#ffffff' | '#111111' {
  const bg = relativeLuminance(backgroundHex);
  const contrastWhite = (1 + 0.05) / (bg + 0.05);
  const contrastBlack = (bg + 0.05) / (0 + 0.05);
  return contrastWhite >= contrastBlack ? '#ffffff' : '#111111';
}

/** WCAG contrast ratio between two `#RRGGBB` colors (1 = identical, 21 = max). */
export function contrastRatio(hexA: string, hexB: string): number {
  const L1 = relativeLuminance(hexA);
  const L2 = relativeLuminance(hexB);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG AA for normal text (4.5:1). Large text uses 3:1 — pass `largeText`. */
export function meetsWcagAaContrast(
  foregroundHex: string,
  backgroundHex: string,
  largeText = false,
): boolean {
  return contrastRatio(foregroundHex, backgroundHex) >= (largeText ? 3 : 4.5);
}

function preserveThemeChrome(previous?: TenantTheme) {
  return previous
    ? {
        focusOutlineWidthPx: previous.focusOutlineWidthPx,
        focusOutlineOffsetPx: previous.focusOutlineOffsetPx,
        baseFontScale: previous.baseFontScale,
      }
    : {
        focusOutlineWidthPx: DEFAULT_TENANT_THEME.focusOutlineWidthPx,
        focusOutlineOffsetPx: DEFAULT_TENANT_THEME.focusOutlineOffsetPx,
        baseFontScale: DEFAULT_TENANT_THEME.baseFontScale,
      };
}

export function themeWithPreset(
  preset: TenantThemePresetId,
  previous?: TenantTheme,
): TenantTheme {
  const chrome = preserveThemeChrome(previous);
  if (preset === 'custom') {
    return {
      preset: 'custom',
      accent: previous?.accent ?? DEFAULT_TENANT_THEME.accent,
      ...chrome,
    };
  }
  return { preset, accent: TENANT_THEME_PRESET_ACCENTS[preset], ...chrome };
}

export function themeWithCustomAccent(
  accent: string,
  previous?: TenantTheme,
): TenantTheme {
  const normalized = accent.trim();
  return {
    preset: 'custom',
    accent: isValidAccentColor(normalized) ? normalized : DEFAULT_TENANT_THEME.accent,
    ...preserveThemeChrome(previous),
  };
}

export function serializeTenantTheme(payload: unknown): TenantTheme {
  const normalized = normalizeTenantTheme(payload);
  return {
    preset: normalized.preset,
    accent: resolveTenantAccent(normalized),
    focusOutlineWidthPx: normalized.focusOutlineWidthPx,
    focusOutlineOffsetPx: normalized.focusOutlineOffsetPx,
    baseFontScale: normalized.baseFontScale,
  };
}
